/**
 * NotificacoesPage — histórico de notificações do Portal Cliente (F3.2).
 *
 * Lista as notificações do Gestor (in-app), com "marcar todas como lidas".
 * Gated pela flag `features.projetosNotificacoes` (via dashboard) — se OFF,
 * mostra estado neutro (a rota existe mas o sino não aparece).
 */
import { Alert, Badge, Button, EmptyState } from '@/components/ui';
import { useNavigate } from 'react-router-dom';

import { PortalPage, PortalCarregando } from '@/components/cliente/PortalPage';
import {
  useClienteDashboard,
  useClienteNotificacoes,
  useMarcarNotificacaoLida,
  useMarcarTodasNotificacoesLidas,
  type ClienteNotificacao,
} from '@/lib/cliente-portal-store';

import { formatarData } from '@/lib/format-date';
function formatQuando(iso: string): string {
  try {
    return formatarData(iso, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function NotificacoesPage() {
  const dash = useClienteDashboard();
  const featureOn = dash.data?.features?.projetosNotificacoes === true;
  const { data, isLoading, isError } = useClienteNotificacoes(featureOn);
  const marcarLida = useMarcarNotificacaoLida();
  const marcarTodas = useMarcarTodasNotificacoesLidas();
  const navigate = useNavigate();

  const items = data?.items ?? [];
  const naoLidas = data?.naoLidas ?? 0;

  return (
    <PortalPage
      testId="notificacoes-page"
      title="Notificações"
      subtitle="Avisos sobre o andamento dos seus projetos."
      actions={
        naoLidas > 0 ? (
          <Button size="sm" variant="secondary" onClick={() => marcarTodas.mutate()}>
            Marcar todas como lidas
          </Button>
        ) : undefined
      }
    >
      {!featureOn ? (
        <EmptyState
          title="Notificações ainda não estão ligadas para você"
          description="Quando a IGM ativar os avisos do portal para o seu condomínio, eles chegam aqui e no sininho lá em cima."
        />
      ) : isLoading ? (
        <PortalCarregando linhas={2} rotulo="Carregando as notificações…" />
      ) : isError ? (
        <Alert variant="danger">
          Não conseguimos carregar seus avisos agora. Tente atualizar a página.
        </Alert>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhum aviso por aqui ainda"
          description="Quando houver novidade em algum dos seus projetos, você fica sabendo por aqui."
        />
      ) : (
        <ul className="space-y-2">
          {items.map((n) => (
            <NotifRow
              key={n.id}
              notif={n}
              onOpen={() => {
                if (!n.lida) marcarLida.mutate(n.id);
                // F3.2: toda notificação clicável — link próprio ou fallback projetos.
                navigate(n.linkUrl ?? '/cliente/projetos');
              }}
            />
          ))}
        </ul>
      )}
    </PortalPage>
  );
}

function NotifRow({ notif: n, onOpen }: { notif: ClienteNotificacao; onOpen: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={`w-full rounded-xl border bg-paper-surface p-4 text-left transition hover:shadow-card-hover dark:bg-paper-dark-surface ${
          n.lida
            ? 'border-line-soft dark:border-line-soft-dark'
            : 'border-red/40 dark:border-red-glow/40'
        }`}
        data-testid={`notif-row-${n.id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium text-ink-1 dark:text-ink-dark-1">{n.title}</div>
            <div className="text-sm text-ink-2 dark:text-ink-dark-2 mt-0.5">{n.body}</div>
            <div className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
              {formatQuando(n.criadoEmIso)}
            </div>
          </div>
          {!n.lida && (
            <Badge variant="danger" size="sm">
              Nova
            </Badge>
          )}
        </div>
      </button>
    </li>
  );
}
